/**
 * Pinned review comments — controller (UX Pack F5b). One brain for both
 * surfaces: the builder (member api via templates/auth-api.js) and the review
 * shell (guest api via templates/review-api.js) inject their API seam and
 * capability flags; nothing in here knows which world it is in.
 *
 * Responsibilities: comment MODE (a bridge click interceptor — the single
 * `_onMessage` choke point — consumes canvas clicks before any edit consumer
 * sees them), the thread cache with delta polling (cursor = the server `now`;
 * poll runs only while the sidebar is open, pauses on tab hide, backs off on
 * failure, and fully stops on teardown), anchor lifecycle (compute at pin
 * time; RESOLVE per current state at render time — orphan state is computed,
 * never stored; re-anchoring is an explicit member action elsewhere), and the
 * unresolved-count watermark for the toolbar dot.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import { reportError } from '../core/report-error.js';
import { resolveSectionByInstance } from '../editing/section-utils.js';
import { computeCommentAnchor, resolveCommentAnchor } from '../shared/comment-anchor.js';
import { canonicalizeAssetBackedMediaHTML } from '../shared/asset-html.js';
import { sha256Hex } from '../shared/site-deploy.js';
import {
  verifyEditableIdRepairs,
  commentAnchorFingerprint,
  projectCommentAnchor
} from '../shared/editable-id-repair.js';

const POLL_MS = 60_000;
const POLL_BACKOFF_MAX_MS = 8 * 60_000;

let _api = null;
// canPin = this surface can ARM comment mode, i.e. it has a canvas bridge whose
// clicks can be intercepted. The builder does; the guest review shell composes
// from its own sandboxed-frame handler and must never render the toggle.
let _capabilities = { canResolve: true, canDelete: true, canPin: true };
let _threads = [];
let _cursor = null;
let _pollTimer = null;
let _pollBackoff = POLL_MS;
let _pollActive = false;
let _pollInFlight = false;
let _refreshController = null;
let _refreshRequestId = 0;
let _mode = false;
let _installed = false;
let _disabledLatch = false;
let _offs = [];
let _getProjectKey = null;
let _projectKey = null;
let _activeThreadId = null;
let _navigator = null;
let _navSeq = 0;
const _identityResolutions = new Map();

function emitChanged() {
  eventBus.emit('comments:changed', {
    threads: _threads.slice(),
    openCount: _threads.filter((t) => t.status === 'open').length,
    mode: _mode,
    disabled: _disabledLatch
  });
}

function watermarkKey() {
  const projectId = store.getState()?.projectId || 'unknown';
  return `pb:comments-seen:${projectId}`;
}

/** Open threads newer than the last time the user opened the sidebar. */
export function getUnseenOpenCount() {
  let seen = 0;
  try {
    seen = Number(localStorage.getItem(watermarkKey())) || 0;
  } catch {
    /* private mode: dot shows all open threads */
  }
  return _threads.filter((t) => t.status === 'open' && t.updatedAt > seen).length;
}

export function markCommentsSeen() {
  try {
    localStorage.setItem(watermarkKey(), String(Date.now()));
  } catch {
    /* optional preference */
  }
  emitChanged();
}

export function getCommentThreads() {
  return _threads.slice();
}

/**
 * Stable display numbers, 1-based, oldest first (F5c). Sorts its OWN copy —
 * the server lists newest-first and `_threads` order is never a contract.
 * Pins and the sidebar both read this map, so they cannot disagree; the
 * "Open only" filter hides threads but never renumbers the rest.
 */
export function getThreadNumbers() {
  const ordered = _threads
    .slice()
    .sort(
      (a, b) => Number(a.createdAt) - Number(b.createdAt) || (String(a.id) < String(b.id) ? -1 : 1)
    );
  return new Map(ordered.map((thread, i) => [thread.id, i + 1]));
}

/** The thread highlighted on both surfaces (pin ping + card ring). */
export function getActiveCommentThread() {
  return _activeThreadId;
}

export function setActiveCommentThread(threadId) {
  const next = threadId || null;
  if (next === _activeThreadId) return;
  _activeThreadId = next;
  eventBus.emit('comments:active-thread', { threadId: _activeThreadId });
}

/**
 * Page switching, INJECTED (B0.5). The builder wires page-manager in; the
 * guest review shell wires nothing, because it must not pull section-manager
 * (a mutation writer) into its graph to satisfy one navigation case.
 * @param {{setActivePage: (pageId: string) => Promise<void>} | null} nav
 */
export function setCommentNavigator(nav) {
  _navigator = nav && typeof nav.setActivePage === 'function' ? nav : null;
}

/** Resolve the anchor and scroll the canvas to it. Orphans get no scroll. */
function scrollToThread(thread) {
  const resolution = resolveThreadAnchor(thread);
  if (resolution.kind === 'element') {
    bridge.send('scroll-to-eid', { instanceId: thread.instanceId, eid: resolution.eid });
  } else if (resolution.kind === 'section') {
    bridge.send('scroll-to-section', { instanceId: thread.instanceId });
  }
  // orphaned: nothing to scroll to — the card's warning note is the disclosure
}

/** Resolve once the iframe reports a live bridge, or bail after `ms`. */
function waitForBridgeReady(ms = 3000) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      off();
      resolve();
    };
    const off = eventBus.on('iframe:bridge-ready', finish);
    // A switch that renders nothing new must not hang the caller forever.
    const timer = setTimeout(finish, ms);
  });
}

/**
 * THE one way to go to a thread — used by sidebar cards, pins, the rail, the
 * minimap and the stepper, so they cannot drift apart.
 *
 * Threads are project-wide (the server lists every page's), but pins and the
 * canvas only ever show the ACTIVE page. Before this existed, activating a
 * thread from another page sent `scroll-to-section` for an instance that was
 * not in the document and silently did nothing.
 */
export async function activateThreadAndNavigate(thread, { pan = true } = {}) {
  if (!thread) return;
  setActiveCommentThread(thread.id);
  if (!pan) return;

  const seq = ++_navSeq;
  const activePage = store.getState()?.activePage || null;
  if (thread.pageId && activePage && thread.pageId !== activePage) {
    if (!_navigator) return; // no page switching available (guest shell)
    await _navigator.setActivePage(thread.pageId);
    if (seq !== _navSeq) return; // a newer activation superseded this one
    if (store.getState()?.activePage !== thread.pageId) return; // stale/deleted page id
    await waitForBridgeReady();
    if (seq !== _navSeq) return;
    if (store.getState()?.activePage !== thread.pageId) return;
  }
  if (resolveThreadAnchor(thread).kind === 'pending') {
    await resolveThreadAnchorAsync(thread);
    if (seq !== _navSeq) return;
  }
  scrollToThread(thread);
}

export function isCommentModeActive() {
  return _mode;
}

export function isCommentsDisabled() {
  return _disabledLatch;
}

/**
 * Resolve one thread's anchor against the CURRENT state. Returns
 * {kind:'element', eid, tier} | {kind:'section'} | {kind:'orphaned'} — a
 * per-viewed-version verdict, never persisted.
 */
export function resolveThreadAnchor(thread) {
  // No live store (the guest review shell renders a pinned bundle without
  // initializing one): report the neutral section pinning, never "orphaned" —
  // orphan is a verdict about THIS version's content, which we cannot give.
  if (!store.getState()) return { kind: 'section' };
  const resolvedSection = resolveSectionByInstance(thread.instanceId);
  if (!resolvedSection?.section) return { kind: 'orphaned' };
  if (!thread.anchorJson) return { kind: 'section' };
  const html = resolvedSection.section.html;
  if (!html) return { kind: 'orphaned' };
  if (needsIdentityResolution(resolvedSection.section, thread)) {
    const entry = identityResolution(resolvedSection.section, thread);
    return entry.result || { kind: 'pending' };
  }
  const resolved = resolveCommentAnchor(html, thread.anchorJson);
  return resolved
    ? { kind: 'element', eid: resolved.eid, tier: resolved.tier }
    : { kind: 'orphaned' };
}

function needsIdentityResolution(section, thread) {
  if (section.editableIdRepairs !== undefined) return true;
  try {
    const anchor =
      typeof thread.anchorJson === 'string' ? JSON.parse(thread.anchorJson) : thread.anchorJson;
    return anchor?.identityRevision != null;
  } catch {
    return true;
  }
}

function identityResolution(section, thread) {
  const projectId = store.getState()?.projectId ?? null;
  const key = JSON.stringify([
    projectId,
    section.instanceId,
    thread.instanceId,
    section.html,
    section.originalHtml,
    section.editableIdRepairs,
    thread.anchorJson
  ]);
  const previous = _identityResolutions.get(thread.id);
  if (previous?.key === key) return previous;
  const snapshot = structuredClone(section);
  const capturedThread = structuredClone(thread);
  const entry = { key, result: null, promise: null };
  if (_identityResolutions.size >= 500)
    _identityResolutions.delete(_identityResolutions.keys().next().value);
  _identityResolutions.set(thread.id, entry);
  entry.promise = (async () => {
    const verified = await verifyEditableIdRepairs(snapshot.editableIdRepairs, {
      instanceId: snapshot.instanceId
    });
    if (!verified.ok) return { kind: 'orphaned', reason: verified.reason };
    const html = canonicalizeAssetBackedMediaHTML(snapshot.html);
    const root = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const counts = new Map();
    for (const element of root.querySelectorAll('[data-pb-eid]')) {
      const eid = element.getAttribute('data-pb-eid');
      counts.set(eid, (counts.get(eid) || 0) + 1);
    }
    const projection = projectCommentAnchor({
      section: snapshot,
      projectId,
      thread: capturedThread,
      anchorFingerprint: await commentAnchorFingerprint(capturedThread.anchorJson),
      currentOriginalHtmlHash: await sha256Hex(
        canonicalizeAssetBackedMediaHTML(snapshot.originalHtml)
      ),
      eidCounts: counts
    });
    if (projection.kind === 'unresolved') return { kind: 'orphaned', reason: projection.reason };
    if (projection.kind === 'projected')
      return { kind: 'element', eid: projection.anchor.eid, tier: 'repair' };
    const resolved = resolveCommentAnchor(html, projection.anchor);
    return resolved
      ? { kind: 'element', eid: resolved.eid, tier: resolved.tier }
      : { kind: 'orphaned' };
  })()
    .catch((error) => {
      reportError('Comment identity', error, { user: 'silent' });
      return { kind: 'orphaned', reason: 'invalid-identity-evidence' };
    })
    .then((result) => {
      entry.result = result;
      if (
        _identityResolutions.get(thread.id) === entry &&
        store.getState()?.projectId === projectId
      )
        emitChanged();
      return result;
    });
  return entry;
}

/** Navigation can await verification; stale results never select a new state. */
export async function resolveThreadAnchorAsync(thread) {
  const initial = resolveThreadAnchor(thread);
  if (initial.kind !== 'pending') return initial;
  const entry = _identityResolutions.get(thread.id);
  await entry.promise;
  const current = resolveThreadAnchor(thread);
  return current.kind === 'pending' ? { kind: 'orphaned', reason: 'project-changed' } : current;
}

async function refresh({ delta = true } = {}) {
  if (!_api || _pollInFlight || _disabledLatch) return;
  if (_getProjectKey && !_projectKey) return;
  const requestId = ++_refreshRequestId;
  const projectKey = _projectKey;
  const controller = new AbortController();
  _refreshController = controller;
  _pollInFlight = true;
  try {
    const out = await _api.list({
      since: delta ? _cursor : null,
      signal: controller.signal
    });
    if (
      controller.signal.aborted ||
      requestId !== _refreshRequestId ||
      (_getProjectKey && projectKey !== _projectKey)
    ) {
      return;
    }
    if (delta && _cursor) {
      const byId = new Map(_threads.map((t) => [t.id, t]));
      for (const thread of out.threads) byId.set(thread.id, thread);
      _threads = Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
    } else {
      _threads = out.threads;
    }
    _cursor = out.now;
    _pollBackoff = POLL_MS;
    if (_activeThreadId && !_threads.some((t) => t.id === _activeThreadId)) {
      setActiveCommentThread(null); // the active thread vanished server-side
    }
    emitChanged();
  } catch (err) {
    if (controller.signal.aborted || err?.name === 'AbortError') return;
    if (err?.status === 503 && err?.code === 'comments_disabled') {
      _disabledLatch = true;
      stopPolling();
      emitChanged();
      return;
    }
    _pollBackoff = Math.min(POLL_BACKOFF_MAX_MS, _pollBackoff * 2);
    reportError('Comments', err, {
      user: 'silent',
      detail: 'comment poll failed; backing off',
      id: 'comments-poll'
    });
  } finally {
    if (requestId === _refreshRequestId) {
      _pollInFlight = false;
      _refreshController = null;
      if (_pollActive && (!_getProjectKey || _projectKey)) schedulePoll();
    }
  }
}

function schedulePoll() {
  clearTimeout(_pollTimer);
  _pollTimer = setTimeout(() => {
    if (document.visibilityState === 'hidden') {
      schedulePoll(); // check again next window; hidden tabs never fetch
      return;
    }
    void refresh();
  }, _pollBackoff);
}

/** Poll only while someone is looking (the sidebar owns this switch). */
export function setCommentPolling(on) {
  _pollActive = !!on;
  if (_pollActive) {
    void refresh();
    schedulePoll();
  } else {
    stopPolling();
  }
}

function stopPolling() {
  clearTimeout(_pollTimer);
  _pollTimer = null;
  _refreshRequestId += 1;
  _refreshController?.abort();
  _refreshController = null;
  _pollInFlight = false;
}

function resetForProject(projectKey) {
  _identityResolutions.clear();
  stopPolling();
  setActiveCommentThread(null);
  _projectKey = projectKey;
  _threads = [];
  _cursor = null;
  _pollBackoff = POLL_MS;
  _disabledLatch = false;
  setCommentMode(false);
  emitChanged();
  if (_projectKey) void refresh({ delta: false });
}

function interceptor(type, payload) {
  if (!_mode) return false;
  if (type === 'canvas-clicked') return false; // deselect still works
  if (!payload?.instanceId) return false;
  eventBus.emit('comments:compose-request', {
    instanceId: payload.instanceId,
    eid:
      type === 'element-clicked' && payload.targetRef?.domain === 'dom'
        ? payload.eid || null
        : null,
    pageId: store.getState()?.activePage || null
  });
  return true; // consumed — no edit consumer ever sees this click
}

export function setCommentMode(on) {
  const next = !!on && !_disabledLatch;
  if (next === _mode) return _mode;
  _mode = next;
  bridge.setClickInterceptor(_mode ? interceptor : null);
  document.querySelector('.pb-iframe-wrap')?.classList.toggle('pb-comment-mode', _mode);
  // The crosshair needs BOTH halves: the class above styles the iframe element
  // and the glass overlay, but a parent cursor never reaches the document
  // inside the frame — where EDITING_CSS's `[data-pb-editable] { cursor: grab }`
  // would otherwise take over the moment the pointer moves onto content.
  bridge.send('set-comment-mode', { active: _mode });
  emitChanged();
  return _mode;
}

/** Compose → create. `eid` null = section-level pin (no anchor). */
export async function createThreadAt({ instanceId, eid, pageId, body }) {
  const epoch = store.getEpoch();
  const principal = store.getPrincipalScope();
  const api = _api;
  const html = canonicalizeAssetBackedMediaHTML(
    resolveSectionByInstance(instanceId)?.section?.html
  );
  const anchor = eid && html ? computeCommentAnchor(html, eid) : null;
  if (eid && !anchor)
    throw new Error(
      'This element has a missing or ambiguous duplicate ID. Repair its identity before pinning a comment.'
    );
  if (anchor) {
    anchor.sourceSectionHash = await sha256Hex(html);
    if (store.getEpoch() !== epoch || store.getPrincipalScope() !== principal || _api !== api)
      throw new Error('The project changed while preparing this comment. Try pinning it again.');
  }
  const state = store.getState();
  await api.createThread({
    pageId: pageId || state?.activePage || '',
    instanceId,
    anchorJson: anchor ? JSON.stringify(anchor) : null,
    body,
    createdOnVersion: Number(state?.cloudVersion) || null
  });
  await refresh({ delta: false });
}

export async function replyToThread(threadId, body) {
  await _api.reply(threadId, body);
  await refresh();
}

export async function setThreadStatus(threadId, status) {
  if (!_capabilities.canResolve) throw new Error('Reviewers cannot resolve threads');
  await _api.setStatus(threadId, status);
  await refresh();
}

export async function deleteThread(threadId) {
  if (!_capabilities.canDelete) throw new Error('Reviewers cannot delete threads');
  await _api.deleteThread(threadId);
  _threads = _threads.filter((t) => t.id !== threadId);
  if (_activeThreadId === threadId) setActiveCommentThread(null);
  emitChanged();
  await refresh({ delta: false });
}

export function getCommentCapabilities() {
  return { ..._capabilities };
}

/**
 * @param {{api: {list, createThread, reply, setStatus?, deleteThread?},
 *   capabilities?: {canResolve?: boolean, canDelete?: boolean},
 *   getProjectKey?: (state: object|null) => string|null,
 *   showToast?: Function}} deps
 * @returns {() => void} uninstall (clears the interceptor + poll — teardown
 *   is part of the interceptor contract)
 */
export function initCommentController({ api, capabilities = {}, getProjectKey = null } = {}) {
  if (_installed) return () => {};
  _installed = true;
  _api = api;
  _capabilities = { canResolve: true, canDelete: true, canPin: true, ...capabilities };
  _threads = [];
  _cursor = null;
  _disabledLatch = false;
  _getProjectKey = typeof getProjectKey === 'function' ? getProjectKey : null;
  _projectKey = _getProjectKey ? _getProjectKey(store.getState()) || null : null;

  _offs = [
    eventBus.on('state-changed', (state) => {
      if (!_getProjectKey) return;
      const nextProjectKey = _getProjectKey(state) || null;
      if (nextProjectKey !== _projectKey) resetForProject(nextProjectKey);
    }),
    // A re-render builds a fresh document, which drops the body class — so the
    // crosshair has to be re-pushed or comment mode silently reverts to the
    // editing cursors mid-session (same contract as set-ai-pick-mode).
    eventBus.on('iframe:bridge-ready', () => {
      if (_mode) bridge.send('set-comment-mode', { active: true });
    })
  ];
  if (!_getProjectKey || _projectKey) void refresh({ delta: false });

  return () => {
    _identityResolutions.clear();
    setCommentMode(false);
    stopPolling();
    setActiveCommentThread(null);
    _pollActive = false;
    for (const off of _offs) if (typeof off === 'function') off();
    _offs = [];
    _installed = false;
    _api = null;
    _getProjectKey = null;
    _projectKey = null;
  };
}

/** Test hook. */
export function _resetCommentControllerForTests() {
  _identityResolutions.clear();
  setCommentMode(false);
  stopPolling();
  _pollActive = false;
  _installed = false;
  _api = null;
  _getProjectKey = null;
  _projectKey = null;
  _threads = [];
  _cursor = null;
  _disabledLatch = false;
  _activeThreadId = null;
}
